import type {TaillightsController} from '@github-ui/taillights/controller'
import type {PreparedView, SearchResult, SourceSelection} from '@github-ui/taillights-worker/document'

import type {SourceRange} from './source-navigation'

export type NavigationIntent = {
  revision: number
  collapsed: readonly number[]
  selection: SourceSelection | null
  expandSelection: boolean
  /** The range to unfold instead of the selection. */
  reveal?: SourceRange
  focus: boolean
  scroll: boolean
  scrollLine?: number
  viewportOffset?: number
  position?: {top: number; left: number}
  announceFolds?: boolean
  cancelled?: boolean
}

export type NavigationState = {
  intent: NavigationIntent
  view: PreparedView | null
  preparedFor: TaillightsController | null
  applied: number
  anchorRange: SourceRange | null
  error: string | null
}

export const initialNavigationState: NavigationState = {
  intent: {revision: 0, collapsed: [], selection: null, expandSelection: false, focus: false, scroll: false},
  view: null,
  preparedFor: null,
  applied: -1,
  anchorRange: null,
  error: null,
}

export type NavigationAction =
  | {type: 'request'; intent: Omit<NavigationIntent, 'revision'>}
  | {type: 'prepared'; revision: number; view: PreparedView; controller: TaillightsController}
  | {type: 'failed'; revision: number; message: string}
  | {type: 'anchor'; range: SourceRange | null}
  | {type: 'native-selection'; selection: SourceSelection | null; scopePending?: boolean}
  | {type: 'cancel'}

export function sourceNavigationReducer(state: NavigationState, action: NavigationAction): NavigationState {
  switch (action.type) {
    case 'request':
      return {...state, intent: {...action.intent, revision: state.intent.revision + 1}, error: null}
    case 'prepared':
      return action.revision === state.intent.revision
        ? {
            ...state,
            view: action.view,
            preparedFor: action.controller,
            applied: action.revision,
            error: null,
          }
        : state
    case 'failed':
      return action.revision === state.intent.revision ? {...state, error: action.message} : state
    case 'anchor':
      return {...state, anchorRange: action.range}
    case 'cancel': {
      if (state.applied === state.intent.revision) return state
      const revision = state.intent.revision + 1
      return {
        ...state,
        applied: revision,
        intent: {
          revision,
          collapsed: state.view?.collapsed ?? [],
          selection: null,
          expandSelection: false,
          focus: false,
          scroll: false,
          cancelled: true,
        },
      }
    }
    case 'native-selection':
      if (
        state.applied !== state.intent.revision &&
        action.selection &&
        state.intent.selection &&
        action.selection.start.line === state.intent.selection.start.line &&
        action.selection.start.column === state.intent.selection.start.column &&
        action.selection.end.line === state.intent.selection.end.line &&
        action.selection.end.column === state.intent.selection.end.column &&
        action.selection.direction === state.intent.selection.direction
      ) {
        return state
      }
      // A native caret move during preparation is newer than the requested selection.
      return state.applied === state.intent.revision && !action.scopePending
        ? state
        : {
            ...state,
            intent: {
              ...state.intent,
              revision: state.intent.revision + 1,
              selection: action.selection,
              focus: false,
              scroll: false,
              announceFolds: false,
              cancelled: false,
              position: undefined,
            },
          }
  }
}

type SearchState = {
  query: string
  index: number
  revision: number
  focus: boolean
  result: SearchResult | null
}
type SearchAction =
  | {type: 'query'; query: string; revision: number}
  | {type: 'move'; delta: number; revision: number; query: string; index: number}
  | {type: 'result'; revision: number; result: SearchResult}

export function sourceSearchReducer(state: SearchState, action: SearchAction): SearchState {
  switch (action.type) {
    case 'query':
      return action.revision > state.revision
        ? {query: action.query, index: 0, revision: action.revision, focus: false, result: null}
        : state
    case 'move':
      return action.revision > state.revision
        ? {
            query: action.query,
            index: (state.query === action.query ? state.index : action.index) + action.delta,
            revision: action.revision,
            focus: true,
            result: null,
          }
        : state
    case 'result':
      return state.revision === action.revision ? {...state, result: action.result} : state
  }
}
